import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserCanPredeliver } from 'src/hooks/useUserHasListingPermissions';
import { BulkUploadListingBarcodesModal } from 'src/modals/UploadBarcodes';
import { ContentId } from 'src/utils/constants/contentId';
import { CommandOptionType, Event, ListingQuery } from 'src/WebApiController';

import { BulkActionItem } from '../types';
import { LaunchBulkUpdateBarcodes } from './LaunchBulkUpdateBarcodes';

export const useBulkUpdateBarcodes = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { setModal } = useContext(ModalContext);
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const hasPredeliverPermission = useUserCanPredeliver(
    undefined, // listing
    true // forBulkEdit
  );

  const launchModal = useCallback(() => {
    setModal({
      children: (
        <BulkUploadListingBarcodesModal
          event={event}
          filterQueryWithEventIds={filterQueryWithEventIds}
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  }, [event, filterQueryWithEventIds, setModal]);

  const openDialogStableRef = useEventCallback(() => {
    setModal({
      children: (
        <BulkUploadListingBarcodesModal
          event={event}
          filterQueryWithEventIds={filterQueryWithEventIds}
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkUpdateBarcodes,
        onSelect: openDialogStableRef,
      },
      disabled: !hasPredeliverPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkUpdateBarcodes,
        disabled: !hasPredeliverPermission,
      });
    };
  }, [
    hasPredeliverPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasPredeliverPermission) {
      return [];
    }

    return [
      {
        disabled: isLoading,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          launchModal();
          launchModal();
        },
        title: ContentId.UploadBarcodes,
      },
    ];
  }, [hasPredeliverPermission, isLoading, launchModal]);

  return {
    dropDown: (
      <LaunchBulkUpdateBarcodes
        key="bulkUpdateBarcodes"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          launchModal();
        }}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
